import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CoordKind, CountKey, Counts, CrmAuth, EodDayData, EodDayRow, LeadStatusData, LeadStatusRow, MonitorData, MonitorLeadRow, MonitorRange, MonitorRow, MonitorTotals } from './crmApi';
import { HolidayBanner } from './Holidays';
import LeadPanel from './LeadPanel';
import { Empty, Head, LeadRow, Modal, Spinner, TONE, fmtHm, fmtWhen, inputCls, shiftTimes, useCrmMe } from './ui';
import type { Tone } from './ui';
import { nf } from './viz';

// ── The Monitor ──────────────────────────────────────────────
// For Lead Managers, Sales Admins and the admin:
//   Team numbers  for the team and each person in a period, against the days
//                 before: the work (leads given, new or moved; calls; talked
//                 to; not picked, by what they heard), and where the leads got
//                 to: each lead once, in one box (Call later, Cold, Brochure
//                 sent, Direct visit, Future prospect, Poll sent: out and its
//                 status not updated yet, Interested…): where it is now, or for
//                 a period that is over, where it stood at the end of it; today,
//                 who is working and calling now; and each Student
//                 Coordinator's meetings (joined, didn't join, converted)
//   Lead status   where every open lead is right now, and what is due or late
//   Daily EOD     one day's EOD reports: the numbers and what they wrote
// Every number opens the leads behind it. Mirrors the app's crm_monitor_screens.dart.

// neutral: more or fewer is neither good nor bad (the call outcomes)
type Col = { key: CountKey; label: string; short: string; downIsGood?: boolean; neutral?: boolean; tone?: string };
export const COLS: Col[] = [
  { key: 'given', label: 'Leads given', short: 'Given' },
  { key: 'calls', label: 'Calls', short: 'Calls' },
  { key: 'talked', label: 'Talked to', short: 'Talked' },
  { key: 'callLater', label: 'Call later', short: 'Call later', neutral: true },
  { key: 'cold', label: 'Cold leads', short: 'Cold', neutral: true },
  { key: 'pitched', label: 'Brochures sent', short: 'Brochures' },
  { key: 'visits', label: 'Direct visits', short: 'Visits' },
  { key: 'future', label: 'Future prospects', short: 'Future', neutral: true },
  { key: 'meetings', label: 'Meetings booked', short: 'Meetings' },
  { key: 'pollsSent', label: 'Polls sent', short: 'Polls' },
  { key: 'interested', label: 'Interested', short: 'Interested', tone: 'text-green-700' },
  { key: 'notInterested', label: 'Not interested', short: 'Not int.', downIsGood: true },
  { key: 'noResponse', label: 'No response', short: 'No resp.', downIsGood: true },
  { key: 'converted', label: 'Converted', short: 'Converted', tone: 'text-green-700' },
];
const LABELS: Partial<Record<CountKey, string>> = {
  notPicked: 'Not picked', pollsLater: 'Polls set for later today', visitsCame: 'Visits: they came', visitsMissed: "Visits: they didn't come",
  givenNew: 'Leads given: new', givenMoved: 'Leads given: moved from someone else',
};
const colOf = (k: CountKey) => COLS.find(c => c.key === k);
const labelOf = (k: CountKey) => colOf(k)?.label || LABELS[k] || k;
const DAY_COLS: Col[] = [...COLS.slice(0, 3), { key: 'notPicked', label: 'Not picked', short: 'Not picked', neutral: true }, ...COLS.slice(3)];
const valueOf = (r: Partial<Counts> & { given?: number }, k: CountKey) => Number((r as Record<string, number>)[k] || 0);

const RANGES = [
  { key: 'today', label: 'Today' }, { key: 'yesterday', label: 'Yesterday' }, { key: 'week', label: 'This week' },
  { key: 'lastweek', label: 'Last week' }, { key: 'month', label: 'This month' }, { key: 'lastmonth', label: 'Last month' },
];
const chipStyle = (on: boolean) => (on ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' });
const pctOf = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '—');
// "10:05 AM", India time
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();

// "▲ 3 vs Sat 17 Oct" (fewer Not interested / No response is the good way;
// Call later and Cold are neither)
function delta(now: number, before: number, label: string, downIsGood = false, neutral = false): { text: string; tone: string } {
  const d = now - before;
  if (!d) return { text: `= same as ${label}`, tone: 'text-gray-400' };
  const good = downIsGood ? d < 0 : d > 0;
  return { text: `${d > 0 ? '▲' : '▼'} ${nf(Math.abs(d))} vs ${label}`, tone: neutral ? 'text-gray-500' : good ? 'text-green-700' : 'text-red-600' };
}

// Who is working now, and how long since their last call
function nowLine(r: MonitorRow): { text: string; tone: Tone } | null {
  const n = r.now;
  if (!n) return null;
  if (n.onLeave) return { text: 'On leave today', tone: 'gray' };
  if (n.paused) return { text: 'Paused', tone: 'gray' };
  if (n.awayNow && n.halfDay) return { text: `Half day off till ${fmtHm(n.halfDay.to)}`, tone: 'gray' };
  if (!n.onShift) return { text: n.shift ? `Off shift (${shiftTimes(n.shift)})` : 'Not working hours now', tone: 'gray' };
  if (!r.lastCallAt) return { text: 'Working now · no calls yet', tone: 'amber' };
  const mins = Math.max(0, Math.round((Date.now() - new Date(r.lastCallAt).getTime()) / 60000));
  const ago = mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : `${Math.floor(mins / 60)} h ${mins % 60} min ago`;
  return { text: `Working now · last call ${ago}`, tone: mins >= 90 ? 'red' : mins >= 45 ? 'amber' : 'green' };
}

function downloadCsv(file: string, rows: (string | number)[][]) {
  const cell = (v: string | number) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const blob = new Blob([`﻿${rows.map(r => r.map(cell).join(',')).join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = file; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// A number that opens its leads (a zero opens nothing)
const Num: React.FC<{ n: number; onOpen: () => void; tone?: string; strong?: boolean; label: string }> = ({ n, onOpen, tone = 'text-gray-800', strong, label }) => (
  n > 0
    ? <button onClick={onOpen} className={`tabular-nums ${strong ? 'font-black' : 'font-semibold'} ${tone} hover:text-purple-700 hover:underline underline-offset-2`} aria-label={`${label}: ${n}. Show the leads`}>{nf(n)}</button>
    : <span className="tabular-nums text-gray-300">0</span>
);

// What happened, in a few words, for the lists behind the numbers (a
// brochure with its poll time, a visit or meeting with its time)
function what(a: MonitorLeadRow): string {
  if (a.type === 'poll') return `Poll: ${a.result}${a.reason ? ` (${a.reason})` : ''}`;
  if (a.type === 'assigned') return `Given to ${a.toName}`;
  if (a.type === 'moved') return `Moved to ${a.toName}${a.fromName ? ` from ${a.fromName}` : ''}`;
  if (a.outcome === 'Coordinator Meeting' || a.result === 'Coordinator Meeting') return `Coordinator Meeting${a.toName ? ` with ${a.toName}` : ''}${a.nextAt ? `, ${fmtWhen(a.nextAt)}` : ''}`;
  const result = a.result && !(a.result === 'Poll Sent' && a.pollSentAt) ? ` → ${a.result}` : '';
  return `${a.outcome}${a.heard ? ` · ${a.heard}` : ''}${result}${a.result === 'Direct Visit' && a.nextAt ? `, visit ${fmtWhen(a.nextAt)}` : ''}${a.pollSentAt ? ` · Poll time ${fmtWhen(a.pollSentAt)}` : ''}`
    + `${a.visit ? ` · ${a.visit === 'came' ? 'they came' : "they didn't come"}` : ''}${a.reason ? ` (${a.reason})` : ''}`;
}

// The leads behind one number of Team numbers, each with the last thing done
// with it in the period and, for a period that is over, where it is now if it
// has moved on. heard: only the Not Picked calls where they heard that
const MonitorLeads: React.FC<{ auth: CrmAuth; kind: CountKey; person: { id: string; name: string } | null; heard?: string; range: MonitorRange; rangeLabel: string; onClose: () => void; onOpenLead: (id: string) => void }> = ({ auth, kind, person, heard, range, rangeLabel, onClose, onOpenLead }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.monitorLeads>> | null>(null);
  useEffect(() => {
    crm.monitorLeads(auth, { ...range, kind, heard, salesPersonId: person?.id || undefined }).then(setData).catch(e => { toast.error(e.message); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, kind, heard, person?.id, range.range, range.from, range.to]);
  const by = (r: MonitorLeadRow) => (!person && r.byName && r.type !== 'assigned' && r.type !== 'moved' ? ` · by ${r.byName}` : '');
  const view = (r: MonitorLeadRow) => (r.lead ? (
    <LeadRow key={r.id} lead={r.lead} onOpen={() => onOpenLead(r.lead!.id)} showOwner={!person} maxOwners={data?.maxOwners}
      extra={<>
        <p className="text-[11px] text-purple-800 mt-0.5 truncate">{fmtWhen(r.at)} · {what(r)}{by(r)}</p>
        {r.nowStage && <p className="text-[11px] font-bold text-amber-700 mt-0.5">Now: {r.nowStage}</p>}
      </>} />
  ) : (
    <p key={r.id} className="text-xs text-gray-500 bg-white border border-gray-100 rounded-xl px-4 py-2">{r.leadName || `#${r.leadNo}`} · {fmtWhen(r.at)} · {what(r)}</p>
  ));
  return (
    <Modal title={`${labelOf(kind)}${heard ? ` (${heard === 'none' ? 'not recorded' : heard})` : ''}: ${person ? person.name : 'whole team'}`} sub={`${rangeLabel}${data ? ` · ${data.total}` : ''}`} onClose={onClose} wide>
      {!data ? <Spinner label="Loading the leads…" /> : data.rows.length === 0 ? <p className="text-sm text-gray-400">None.</p> : (
        <div className="space-y-2">
          {data.total > data.rows.length && <p className="text-xs text-gray-400">The latest {data.rows.length} of {data.total}.</p>}
          {data.rows.map(r => view(r))}
          <p className="text-[11px] text-gray-400 pt-1">Open a lead to see its history, call it or move it.</p>
        </div>
      )}
    </Modal>
  );
};

// The students behind a coordinator number: their meetings in the period
// (booked / joined / didn't join) or the ones marked Converted in it
const COORD_COLS: { kind: CoordKind; label: string; short: string; tone?: string }[] = [
  { kind: 'booked', label: 'Meetings', short: 'Meetings' },
  { kind: 'joined', label: 'Joined', short: 'Joined', tone: 'text-indigo-700' },
  { kind: 'notJoined', label: "Didn't join", short: "Didn't join", tone: 'text-gray-500' },
  { kind: 'converted', label: 'Converted', short: 'Converted', tone: 'text-green-700' },
];
const CoordLeads: React.FC<{ auth: CrmAuth; kind: CoordKind; coordinator: { id: string; name: string } | null; range: MonitorRange; rangeLabel: string; onClose: () => void; onOpenLead: (id: string) => void }> = ({ auth, kind, coordinator, range, rangeLabel, onClose, onOpenLead }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.meetMonitorLeads>> | null>(null);
  useEffect(() => {
    crm.meetMonitorLeads(auth, { ...range, kind, coordinatorId: coordinator?.id || '' }).then(setData).catch(e => { toast.error(e.message); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, kind, coordinator?.id, range.range, range.from, range.to]);
  const label = COORD_COLS.find(c => c.kind === kind)?.label || kind;
  return (
    <Modal title={`${label}: ${coordinator ? coordinator.name : 'every coordinator'}`} sub={`${rangeLabel}${data ? ` · ${data.total}` : ''}`} onClose={onClose} wide>
      {!data ? <Spinner label="Loading the students…" /> : data.rows.length === 0 ? <p className="text-sm text-gray-400">None.</p> : (
        <div className="space-y-2">
          {data.rows.map(r => {
            const line = `${kind === 'converted' ? 'Converted' : `Meeting ${r.status === 'Not Joined' ? "· didn't join" : r.status === 'Joined' ? '· joined' : `· ${r.status.toLowerCase()}`}`} ${fmtWhen(r.at)}${r.coordinatorName ? ` · ${r.coordinatorName}` : ' · no coordinator yet'}${r.salesPersonName ? ` · booked for ${r.salesPersonName}` : ''}`;
            return r.lead
              ? <LeadRow key={r.id} lead={r.lead} onOpen={() => onOpenLead(r.lead!.id)} showOwner maxOwners={data.maxOwners} extra={<p className="text-[11px] text-indigo-800 mt-0.5 truncate">{line}</p>} />
              : <p key={r.id} className="text-xs text-gray-500 bg-white border border-gray-100 rounded-xl px-4 py-2">{r.leadName || `#${r.leadNo}`} · {line}</p>;
          })}
        </div>
      )}
    </Modal>
  );
};

// Calls hour by hour through one day: who was calling when
const HourGrid: React.FC<{ people: MonitorRow[]; hours: { from: number; to: number } }> = ({ people, hours }) => {
  const rows = people.filter(p => p.hours && p.calls > 0);
  if (!rows.length) return null;
  const max = Math.max(1, ...rows.flatMap(p => p.hours!));
  const cols = Array.from({ length: hours.to - hours.from }, (_, i) => hours.from + i);
  const hourLabel = (h: number) => `${((h + 11) % 12) + 1} ${h < 12 ? 'AM' : 'PM'}`;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Calls hour by hour">
      <h2 className="font-bold text-gray-800">Calls hour by hour</h2>
      <p className="text-xs text-gray-400 mb-3">Darker: more calls in that hour</p>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead><tr>
            <th className="text-left font-bold text-gray-500 pr-3 py-1 sticky left-0 bg-white">Salesperson</th>
            {cols.map(h => <th key={h} className="font-semibold text-gray-400 px-1 py-1 whitespace-nowrap">{hourLabel(h)}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id}>
                <td className="pr-3 py-1 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white">{p.name}</td>
                {p.hours!.map((n, i) => (
                  <td key={i} className="px-0.5 py-0.5">
                    <div className="w-10 h-8 rounded-md flex items-center justify-center font-bold tabular-nums"
                      title={`${p.name}, ${hourLabel(cols[i])}: ${n} call${n === 1 ? '' : 's'}`}
                      style={{ background: n ? `rgba(42,120,214,${0.15 + 0.85 * (n / max)})` : '#f5f4f9', color: n / max > 0.55 ? '#fff' : n ? '#1f1b2d' : '#c9c6d3' }}>{n || ''}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Team numbers ─────────────────────────────────────────────

// The steps, in the order of the call flow
const STEP_ROWS: { title: string; keys: CountKey[] }[] = [
  { title: 'Talked', keys: ['callLater', 'cold'] },
  { title: 'Next step', keys: ['pitched', 'visits', 'future', 'meetings'] },
  { title: 'Poll', keys: ['pollsSent'] },
  { title: 'Poll answer', keys: ['interested', 'notInterested', 'noResponse'] },
  { title: 'Paid', keys: ['converted'] },
];
const STEP_KEYS = STEP_ROWS.flatMap(r => r.keys);
const NOT_RECORDED = 'Not recorded';
const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400';
type ListWhat = { kind: CountKey; person: { id: string; name: string } | null; heard?: string };

// One reason on a Not Picked call: how many, with a bar against the biggest (a zero opens nothing)
const HeardRow: React.FC<{ label: string; n: number; max: number; onOpen: () => void }> = ({ label, n, max, onOpen }) => (
  <button onClick={onOpen} disabled={!n} aria-label={`${label}: ${n}. Show the leads`}
    className={`w-full grid grid-cols-[minmax(0,1fr)_56px_32px] sm:grid-cols-[minmax(0,1fr)_96px_32px] items-center gap-3 py-1.5 px-2 -mx-2 rounded-lg text-left enabled:hover:bg-purple-50 ${focusRing}`}>
    <span className={`text-sm truncate ${n ? 'text-gray-700' : 'text-gray-500'}`}>{label}</span>
    <span className="h-1.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
      {!!n && <span className="block h-full rounded-full" style={{ width: `${Math.max(6, (n / Math.max(1, max)) * 100)}%`, background: '#4e3a69' }} />}
    </span>
    <span className={`text-sm text-right tabular-nums ${n ? 'font-bold text-gray-900' : 'text-gray-400'}`}>{nf(n)}</span>
  </button>
);

// The picked people, or the whole team, by what they heard on Not Picked calls
const HeardPanel: React.FC<{ heard: Record<string, number>; list: NonNullable<MonitorData['heardList']>; onOpen: (key: string) => void }> = ({ heard, list, onOpen }) => {
  const none = heard[''] || 0;
  const max = Math.max(...Object.values(heard), 1);
  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 pt-4 mt-4 border-t border-gray-100">
      <div>
        <h3 className="text-xs font-bold text-gray-500 mb-1">The phone rang</h3>
        {list.rang.map(h => <HeardRow key={h} label={h} n={heard[h] || 0} max={max} onOpen={() => onOpen(h)} />)}
        {!!none && (
          <>
            <h3 className="text-xs font-bold text-gray-500 mt-3 mb-1">Reason not saved</h3>
            <HeardRow label={NOT_RECORDED} n={none} max={max} onOpen={() => onOpen('none')} />
          </>
        )}
      </div>
      <div>
        <h3 className="text-xs font-bold text-gray-500 mb-1">It didn't ring</h3>
        {list.noRing.map(h => <HeardRow key={h} label={h} n={heard[h] || 0} max={max} onOpen={() => onOpen(h)} />)}
      </div>
    </div>
  );
};

// A small number in a person's card, in a row of them
const SmallNum: React.FC<{ n: number; label: string; onOpen: () => void; person: string }> = ({ n, label, onOpen, person }) => (
  <button onClick={onOpen} disabled={!n} aria-label={`${person}, ${label}: ${n}. Show the leads`} className={`text-left rounded-lg px-2 py-1 -mx-1 min-w-0 enabled:hover:bg-purple-50 ${focusRing}`}>
    <span className={`block text-lg font-bold tabular-nums leading-tight ${n ? 'text-gray-900' : 'text-gray-400'}`}>{nf(n)}</span>
    <span className="block text-[11px] text-gray-500 truncate">{label}</span>
  </button>
);

// One person: who they are and whether they are working, the work, then only
// the steps their leads reached. The name opens their numbers.
const PersonCard: React.FC<{ p: MonitorRow; oneDay: boolean; isToday: boolean; picked: boolean; onPick: () => void; onList: (what: ListWhat) => void; heardList?: MonitorData['heardList'] }> = ({ p, oneDay, isToday, picked, onPick, onList, heardList }) => {
  const nl = nowLine(p);
  const who = { id: p.id, name: p.name };
  const steps = STEP_KEYS.filter(k => valueOf(p, k) > 0);
  const heard = heardList ? [...heardList.rang, ...heardList.noRing, ''].filter(h => p.heard?.[h]) : [];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button onClick={onPick} aria-pressed={picked} className={`font-bold text-gray-900 hover:text-purple-700 hover:underline underline-offset-2 text-left rounded ${focusRing}`}>
            {p.name}{!p.active && <span className="text-[11px] font-normal text-gray-500 ml-1">inactive</span>}
          </button>
          <p className="text-xs text-gray-500">{p.role}{!oneDay && p.daysWorked ? ` · called on ${p.daysWorked} day${p.daysWorked === 1 ? '' : 's'}` : ''}</p>
          {nl && <p className={`text-xs font-semibold mt-0.5 ${TONE[nl.tone]}`}>{nl.text}</p>}
          {oneDay && p.firstCallAt && <p className="text-[11px] text-gray-500 mt-0.5">First call {fmtTime(p.firstCallAt)}{!isToday && p.lastCallAt ? ` · last ${fmtTime(p.lastCallAt)}` : ''}</p>}
        </div>
        <div className="text-right shrink-0" title="Converted out of those sent a brochure">
          <p className="text-lg font-bold tabular-nums text-gray-900 leading-tight">{p.pitched || p.converted ? `${p.conversionPct}%` : '—'}</p>
          <p className="text-[11px] text-gray-500">brochure → paid</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-3">
        <SmallNum n={p.given} label="Given" person={p.name} onOpen={() => onList({ kind: 'given', person: who })} />
        <SmallNum n={p.calls} label="Calls" person={p.name} onOpen={() => onList({ kind: 'calls', person: who })} />
        <SmallNum n={p.talked} label="Talked" person={p.name} onOpen={() => onList({ kind: 'talked', person: who })} />
        <SmallNum n={p.notPicked} label="Not picked" person={p.name} onOpen={() => onList({ kind: 'notPicked', person: who })} />
      </div>
      {!!p.given && p.givenNew !== undefined && (
        <p className="text-xs text-gray-500 mt-1 px-1">
          Given: <button onClick={() => onList({ kind: 'givenNew', person: who })} disabled={!p.givenNew} className={`font-semibold text-gray-700 enabled:hover:underline rounded ${focusRing}`}>{nf(p.givenNew)} new</button>
          {' · '}<button onClick={() => onList({ kind: 'givenMoved', person: who })} disabled={!p.givenMoved} className={`font-semibold text-gray-700 enabled:hover:underline rounded ${focusRing}`}>{nf(p.givenMoved || 0)} moved</button>
        </p>
      )}

      {steps.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
          {steps.map(k => (
            <button key={k} onClick={() => onList({ kind: k, person: who })} aria-label={`${p.name}, ${labelOf(k)}: ${valueOf(p, k)}. Show the leads`}
              className={`text-xs rounded-lg bg-gray-50 hover:bg-purple-50 px-2 py-1 text-gray-600 ${focusRing}`}>
              {labelOf(k)} <b className={`tabular-nums ${colOf(k)?.tone || 'text-gray-900'}`}>{nf(valueOf(p, k))}</b>
            </button>
          ))}
        </div>
      )}
      {(!!p.pollsDue || !!p.pollsLater) && (
        <div className="flex flex-wrap gap-x-3 mt-2 text-xs font-bold">
          {!!p.pollsDue && <button onClick={() => onList({ kind: 'pollsSent', person: who })} className={`text-red-600 hover:underline rounded ${focusRing}`}>{p.pollsDue} poll status due</button>}
          {!!p.pollsLater && <button onClick={() => onList({ kind: 'pollsLater', person: who })} className={`text-fuchsia-700 hover:underline rounded ${focusRing}`}>{p.pollsLater} poll{p.pollsLater === 1 ? '' : 's'} later today</button>}
        </div>
      )}
      {heard.length > 0 && (
        <p className="text-xs text-gray-500 mt-2 leading-relaxed">
          Not picked:{' '}
          {heard.map((h, i) => (
            <React.Fragment key={h}>
              {i > 0 && ' · '}
              <button onClick={() => onList({ kind: 'notPicked', person: who, heard: h || 'none' })} className={`hover:underline text-gray-700 rounded ${focusRing}`}>{h || NOT_RECORDED} <b className="tabular-nums">{p.heard?.[h]}</b></button>
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

export const MonitorNumbers: React.FC<{ auth: CrmAuth; onNavigate?: (page: string) => void }> = ({ auth, onNavigate }) => {
  const { me } = useCrmMe(auth);
  const [range, setRange] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [person, setPerson] = useState('');
  const [data, setData] = useState<MonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ListWhat | null>(null);
  const [coordList, setCoordList] = useState<{ kind: CoordKind; coordinator: { id: string; name: string } | null } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const params: MonitorRange = range === 'custom' && from && to ? { from, to } : { range: range === 'custom' ? 'today' : range };

  // Only the latest filter's answer counts: a slower one (a month) can arrive after it
  const asked = useRef(0);
  const load = useCallback(async () => {
    const mine = ++asked.current;
    setLoading(true);
    try {
      const d = await crm.monitor(auth, { ...params, salesPersonId: person || undefined });
      if (mine === asked.current) setData(d);
    } catch (e: any) { if (mine === asked.current) toast.error(e.message); }
    finally { if (mine === asked.current) setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, params.range, params.from, params.to, person]);
  useEffect(() => { load(); }, [load]);
  // Today keeps itself up to date while the page is open
  useEffect(() => {
    if (range !== 'today') return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible' && !list && !coordList && !open) load(); }, 60000);
    return () => clearInterval(t);
  }, [range, load, list, coordList, open]);

  if (!data) return loading ? <Spinner label="Counting…" /> : <Empty icon="⚠️" title="Could not load the numbers" />;
  const t: MonitorTotals = data.team;
  const oneDay = data.range.from === data.range.to;
  const isToday = oneDay && data.range.from === data.today;
  const pickDay = (day: string) => { setFrom(day); setTo(day); setRange('custom'); };
  // The date boxes always show the period on screen; changing one picks dates
  const shownFrom = range === 'custom' && from ? from : data.range.from;
  const shownTo = range === 'custom' && to ? to : data.range.to;
  const changeFrom = (v: string) => { if (!v) return; setFrom(v); setTo(v > shownTo ? v : shownTo); setRange('custom'); };
  const changeTo = (v: string) => { if (!v) return; setTo(v); setFrom(v < shownFrom ? v : shownFrom); setRange('custom'); };
  const people = (me?.people || []).filter(p => p.takesLeads || data.people.some(x => x.id === p.id));
  const answered = t.interested + t.notInterested + t.noResponse;
  // How far the leads got (each is in one box): past the brochure, past the poll
  const pastPoll = t.pollsSent + answered + t.converted;
  const pastBrochure = t.pitched + t.visits + (t.future || 0) + pastPoll;
  const leadsInBoxes = STEP_KEYS.reduce((s, k) => s + valueOf(t, k), 0);
  const csv = () => {
    const heardKeys = data.heardList ? [...data.heardList.rang, ...data.heardList.noRing] : [];
    const head = ['Salesperson', ...COLS.map(c => c.label), 'Given: new', 'Given: moved', 'Not picked', ...heardKeys, NOT_RECORDED, 'Brochure → paid %'];
    const line = (name: string, r: MonitorTotals | MonitorRow) => [name, ...COLS.map(c => valueOf(r, c.key)), r.givenNew || 0, r.givenMoved || 0, r.notPicked, ...heardKeys.map(h => r.heard?.[h] || 0), r.heard?.[''] || 0, r.conversionPct];
    const rows: (string | number)[][] = [[`Team numbers, ${data.range.label}`], head, ...data.people.map(p => line(p.name, p)), line('Team', t)];
    if (data.days.length) rows.push([], ['Day', ...COLS.map(c => c.label), 'Not picked'], ...data.days.map(d => [d.label + (d.holiday ? ` (${d.holiday})` : ''), ...COLS.map(c => valueOf(d, c.key)), d.notPicked]));
    downloadCsv(`team-numbers-${data.range.from}${oneDay ? '' : `-to-${data.range.to}`}.csv`, rows);
  };
  const scope = data.person;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-3 space-y-2.5">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Period">
          {RANGES.map(r => <button key={r.key} onClick={() => setRange(r.key)} aria-pressed={range === r.key} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${focusRing}`} style={chipStyle(range === r.key)}>{r.label}</button>)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">From
            <input type="date" value={shownFrom} max={data.today} onChange={e => changeFrom(e.target.value)} className={`${inputCls} !py-1.5 !w-36`} />
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">To
            <input type="date" value={shownTo} max={data.today} onChange={e => changeTo(e.target.value)} className={`${inputCls} !py-1.5 !w-36`} />
          </label>
          <select value={person} onChange={e => setPerson(e.target.value)} className={`${inputCls} !py-1.5 !w-auto max-w-full`} aria-label="Whose numbers">
            <option value="">Whole team</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="flex gap-2 sm:ml-auto">
            <button onClick={load} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 ${focusRing}`}>↻ Refresh</button>
            <button onClick={csv} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 ${focusRing}`}>⬇ Download</button>
          </div>
        </div>
      </div>

      {/* What is on screen, in words, so a filter is never in doubt */}
      <div className="px-1" aria-live="polite">
        <p className="font-bold text-gray-900">{data.range.label} · {scope ? scope.name : 'Whole team'}{loading && <span className="font-normal text-xs text-gray-500 ml-2">Loading…</span>}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {data.over
            ? `Each lead is where it stood at the end of ${oneDay ? 'this day' : 'this period'}, so these numbers won't change. Tap a number to see each lead and where it is now.`
            : 'Each lead is where it is right now. Tap a number to see the leads.'}
        </p>
      </div>

      {isToday && <HolidayBanner note={data.holiday} manager onOpen={onNavigate ? () => onNavigate('team:holidays') : undefined} />}

      <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
        {/* The work */}
        <div className="p-4 sm:p-5 bg-white rounded-2xl border border-gray-100" role="region" aria-label="The work">
          <h2 className="font-bold text-gray-900 mb-3">The work</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
            {(['given', 'calls', 'talked', 'notPicked'] as CountKey[]).map(k => {
              const n = valueOf(t, k);
              const d = delta(n, valueOf(data.before, k), data.compare.label, k === 'notPicked', k === 'given');
              return (
                <div key={k} className="min-w-0">
                  <button onClick={() => setList({ kind: k, person: scope })} disabled={!n} aria-label={`${labelOf(k)}: ${n}. Show the leads`} className={`text-left rounded-lg enabled:hover:text-purple-700 ${focusRing}`}>
                    <span className="block text-xs font-semibold text-gray-500">{labelOf(k)}</span>
                    <span className="block text-3xl font-black tabular-nums text-gray-900 leading-tight">{nf(n)}</span>
                  </button>
                  {k === 'given' && t.givenNew !== undefined ? (
                    <p className="text-xs text-gray-500 mt-0.5" title="A lead counts once in the period, however many people it moved through">
                      <button onClick={() => setList({ kind: 'givenNew', person: scope })} disabled={!t.givenNew} className={`font-semibold text-gray-700 enabled:hover:underline rounded ${focusRing}`}>{nf(t.givenNew)} new</button>
                      {' · '}<button onClick={() => setList({ kind: 'givenMoved', person: scope })} disabled={!t.givenMoved} className={`font-semibold text-gray-700 enabled:hover:underline rounded ${focusRing}`}>{nf(t.givenMoved || 0)} moved</button>
                    </p>
                  ) : <p className={`text-[11px] font-semibold mt-0.5 ${d.tone}`}>{d.text}</p>}
                </div>
              );
            })}
          </div>
          {data.heardList && t.heard && !!t.notPicked && (
            <HeardPanel heard={t.heard} list={data.heardList} onOpen={h => setList({ kind: 'notPicked', person: scope, heard: h })} />
          )}
        </div>

        {/* Each lead once, in the order of the call flow */}
        <div className="p-4 sm:p-5 bg-white rounded-2xl border border-gray-100" role="region" aria-label="Where the leads got to">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 mb-1">
            <h2 className="font-bold text-gray-900">Where the leads got to</h2>
            <p className="text-xs text-gray-500 tabular-nums">{nf(leadsInBoxes)} lead{leadsInBoxes === 1 ? '' : 's'}, each counted once</p>
          </div>
          <div className="divide-y divide-gray-100">
            {STEP_ROWS.map(row => (
              <div key={row.title} className="grid sm:grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-1 py-3">
                <h3 className="text-xs font-bold text-gray-500 sm:pt-1">{row.title}</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
                  {row.keys.map(k => {
                    const c = colOf(k);
                    const n = valueOf(t, k);
                    const d = delta(n, valueOf(data.before, k), data.compare.label, c?.downIsGood, c?.neutral);
                    return (
                      <div key={k} className="min-w-0">
                        <button onClick={() => setList({ kind: k, person: scope })} disabled={!n} aria-label={`${labelOf(k)}: ${n}. Show the leads`} className={`text-left rounded-lg w-full enabled:hover:text-purple-700 ${focusRing}`}>
                          <span className={`block text-2xl font-black tabular-nums leading-tight ${n ? c?.tone || 'text-gray-900' : 'text-gray-400'}`}>{nf(n)}</span>
                          <span className="block text-xs font-semibold text-gray-600 truncate">{labelOf(k)}</span>
                        </button>
                        <p className={`text-[11px] font-semibold truncate ${d.tone}`} title={d.text}>{d.text}</p>
                        {k === 'pitched' && !!t.pollsLater && <button onClick={() => setList({ kind: 'pollsLater', person: scope })} className={`text-[11px] font-bold text-fuchsia-700 hover:underline rounded ${focusRing}`}>{nf(t.pollsLater)} poll{t.pollsLater === 1 ? '' : 's'} later today</button>}
                        {k === 'pollsSent' && !!t.pollsDue && <button onClick={() => setList({ kind: 'pollsSent', person: scope })} className={`text-[11px] font-bold text-red-600 hover:underline rounded ${focusRing}`}>{nf(t.pollsDue)} status due now</button>}
                        {k === 'visits' && (!!t.visitsCame || !!t.visitsMissed) && (
                          <p className="text-[11px] text-gray-500">
                            <button onClick={() => setList({ kind: 'visitsCame', person: scope })} disabled={!t.visitsCame} className={`enabled:hover:underline rounded ${focusRing}`}>{nf(t.visitsCame || 0)} came</button>
                            {' · '}<button onClick={() => setList({ kind: 'visitsMissed', person: scope })} disabled={!t.visitsMissed} className={`enabled:hover:underline rounded ${focusRing}`}>{nf(t.visitsMissed || 0)} didn't</button>
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600 pt-3 border-t border-gray-100">
            <span title="Leads that got the brochure or further, out of those talked to">Talked → brochure <b className="text-gray-900">{pctOf(pastBrochure, t.talked)}</b></span>
            <span title="Leads whose poll went out or further, out of those that got the brochure">Brochure → poll <b className="text-gray-900">{pctOf(pastPoll, pastBrochure)}</b></span>
            <span>Poll answers: interested <b className="text-gray-900">{pctOf(t.interested, answered)}</b></span>
            <span>Brochure → paid <b className="text-gray-900">{t.pitched || t.converted ? `${t.conversionPct}%` : '—'}</b></span>
          </div>
        </div>

        {/* Each salesperson */}
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 px-1 mb-2">
            <h2 className="font-bold text-gray-900">{isToday ? 'Each person, working now' : 'Each person'}</h2>
            {scope
              ? <button onClick={() => setPerson('')} className={`text-xs font-bold text-purple-700 hover:underline rounded ${focusRing}`}>Back to the whole team</button>
              : <p className="text-xs text-gray-500">Tap a name to open their numbers</p>}
          </div>
          {data.people.length === 0 ? <p className="text-sm text-gray-500 px-1">Nobody is taking calls yet.</p> : (
            <div className="grid lg:grid-cols-2 gap-3">
              {data.people.map(p => <PersonCard key={p.id} p={p} oneDay={oneDay} isToday={isToday} picked={person === p.id} onPick={() => setPerson(person === p.id ? '' : p.id)} onList={setList} heardList={data.heardList} />)}
            </div>
          )}
        </div>

        {/* Each Student Coordinator: their meetings in the period and what came of them */}
        {data.coordinators && (data.coordinators.rows.length > 0 || data.coordinators.team.booked > 0) && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Student Coordinators">
            <h2 className="font-bold text-gray-900">Student Coordinators</h2>
            <p className="text-xs text-gray-500 mb-3">
              {data.range.label}: the meetings in it (by their time), whether the student joined, and the students each coordinator marked Converted. Conversion: of the students who joined, how many have paid.
              {data.coordinators.open > 0 && <b className="text-amber-700"> {data.coordinators.open} still open: no coordinator took {data.coordinators.open === 1 ? 'it' : 'them'} yet.</b>}
            </p>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-[11px] text-gray-500">
                  <th className="text-left py-2 px-2 font-bold">Coordinator</th>
                  {COORD_COLS.map(c => <th key={c.kind} className="text-right py-2 px-2 font-bold whitespace-nowrap">{c.short}</th>)}
                  <th className="text-right py-2 px-2 font-bold whitespace-nowrap" title="Of the students who joined, how many have paid">Conversion</th>
                  <th className="text-right py-2 px-2 font-bold whitespace-nowrap">To mark</th>
                </tr></thead>
                <tbody>
                  {data.coordinators.rows.map(r => (
                    <tr key={r.id} className="border-t border-gray-100">
                      <td className="py-2 px-2"><b className="text-gray-800">{r.name}</b>{!r.active && <span className="text-[10px] text-gray-500 ml-1">inactive</span>}</td>
                      {COORD_COLS.map(c => (
                        <td key={c.kind} className="py-2 px-2 text-right">
                          <Num n={r[c.kind]} tone={c.tone} label={`${r.name}, ${c.label}`} onOpen={() => setCoordList({ kind: c.kind, coordinator: { id: r.id, name: r.name } })} />
                        </td>
                      ))}
                      <td className="py-2 px-2 text-right tabular-nums">{r.joined ? `${r.conversionPct}%` : '—'}</td>
                      <td className={`py-2 px-2 text-right tabular-nums ${r.toMark ? 'font-bold text-red-600' : 'text-gray-400'}`}>{r.toMark}</td>
                    </tr>
                  ))}
                  {data.coordinators.rows.length > 1 && (
                    <tr className="border-t-2 border-gray-200 bg-gray-50/60">
                      <td className="py-2 px-2 font-black text-gray-900">All</td>
                      {COORD_COLS.map(c => <td key={c.kind} className="py-2 px-2 text-right"><Num n={data.coordinators!.team[c.kind]} tone={c.tone} strong label={`All coordinators, ${c.label}`} onOpen={() => setCoordList({ kind: c.kind, coordinator: null })} /></td>)}
                      <td className="py-2 px-2 text-right tabular-nums font-bold">{data.coordinators.team.joined ? `${data.coordinators.team.conversionPct}%` : '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold">{data.coordinators.team.toMark}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.hours && <HourGrid people={data.people} hours={data.hours} />}

        {/* Day by day */}
        {data.days.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Day by day">
            <h2 className="font-bold text-gray-900">Day by day</h2>
            <p className="text-xs text-gray-500 mb-3">{scope ? scope.name : 'The whole team'}. Each day shows where its leads stood at the end of that day. Tap a day to open it.</p>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[1120px]">
                <thead><tr className="text-[11px] text-gray-500">
                  <th className="text-left py-2 px-2 font-bold sticky left-0 bg-white">Day</th>
                  {DAY_COLS.map(c => <th key={c.key} className="text-right py-2 px-2 font-bold whitespace-nowrap">{c.short}</th>)}
                </tr></thead>
                <tbody>
                  {data.days.filter(d => d.working || d.calls || d.given).map(d => (
                    <tr key={d.day} onClick={() => pickDay(d.day)} className={`group border-t border-gray-100 cursor-pointer hover:bg-purple-50 ${d.working ? '' : 'text-gray-500'}`}>
                      <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 bg-white group-hover:bg-purple-50">
                        <button onClick={e => { e.stopPropagation(); pickDay(d.day); }} className={`font-semibold rounded hover:text-purple-700 ${focusRing}`}>{d.label}</button>
                        {d.holiday ? <span className="text-[11px] text-amber-700 ml-1.5">{d.holiday}</span> : !d.working ? <span className="text-[11px] ml-1.5">day off</span> : null}
                      </td>
                      {DAY_COLS.map(c => <td key={c.key} className={`py-1.5 px-2 text-right tabular-nums ${valueOf(d, c.key) ? c.tone || '' : 'text-gray-400'}`}>{nf(valueOf(d, c.key))}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {list && me && (
        <MonitorLeads auth={auth} kind={list.kind} person={list.person} heard={list.heard} range={params} rangeLabel={data.range.label}
          onClose={() => setList(null)} onOpenLead={setOpen} />
      )}
      {coordList && me && (
        <CoordLeads auth={auth} kind={coordList.kind} coordinator={coordList.coordinator} range={params} rangeLabel={data.range.label}
          onClose={() => setCoordList(null)} onOpenLead={setOpen} />
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

// ── Lead status ──────────────────────────────────────────────

const STATUS_COLS: { kind: string; short: string; label: string }[] = [
  { kind: 'New', short: 'New', label: 'New, not called' },
  { kind: 'Not Picked', short: 'Not picked', label: 'Not Picked' },
  { kind: 'Call Later', short: 'Call later', label: 'Call Later' },
  { kind: 'Meeting Booked', short: 'Meeting', label: 'Meeting Booked: coordinator meeting ahead' },
  { kind: 'P2P', short: 'P2P', label: 'P2P: with a Lead Manager (Path to Placement)' },
  { kind: 'Poll Sent', short: 'Poll sent', label: 'Poll Sent: status to update' },
  { kind: 'Interested', short: 'Interested', label: 'Interested: waiting to pay' },
  { kind: 'No Response', short: 'No resp.', label: 'No Response' },
  { kind: 'Future Prospect', short: 'Future', label: 'Future Prospect' },
  { kind: 'Direct Visit', short: 'Visit', label: 'Direct Visit' },
];
const STATUS_LABEL: Record<string, string> = {
  open: 'Open leads', toCall: 'New leads not called', pollsDue: 'Polls to update now', pollsLate: 'Polls gone late',
  paymentsDue: 'Payments due', late: 'Follow-ups gone late', waiting: 'Waiting for someone',
  ...Object.fromEntries(STATUS_COLS.map(c => [c.kind, c.label])),
};

const StatusLeads: React.FC<{ auth: CrmAuth; kind: string; person: { id: string; name: string } | null; onClose: () => void; onOpenLead: (id: string) => void }> = ({ auth, kind, person, onClose, onOpenLead }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.statusLeads>> | null>(null);
  useEffect(() => {
    crm.statusLeads(auth, kind, person?.id || '').then(setData).catch(e => { toast.error(e.message); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, kind, person?.id]);
  return (
    <Modal title={`${STATUS_LABEL[kind] || kind}: ${person ? person.name : 'whole team'}`} sub={data ? `${data.leads.length} lead${data.leads.length === 1 ? '' : 's'}` : undefined} onClose={onClose} wide>
      {!data ? <Spinner label="Loading the leads…" /> : data.leads.length === 0 ? <p className="text-sm text-gray-400">None right now.</p> : (
        <div className="space-y-2">
          {data.leads.map(l => <LeadRow key={l.id} lead={l} onOpen={() => onOpenLead(l.id)} showOwner={!person} maxOwners={data.maxOwners} />)}
        </div>
      )}
    </Modal>
  );
};

export const LeadStatusView: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const { me } = useCrmMe(auth);
  const [data, setData] = useState<LeadStatusData | null>(null);
  const [list, setList] = useState<{ kind: string; person: { id: string; name: string } | null } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.leadStatus(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner label="Loading where every lead is…" />;
  const t = data.team;
  const show = (kind: string, row?: LeadStatusRow) => setList({ kind, person: row ? { id: row.id, name: row.name } : null });
  const tiles: { kind: string; label: string; n: number; sub?: string; tone?: string }[] = [
    { kind: 'open', label: 'Open leads', n: t.open, sub: 'with a salesperson now' },
    { kind: 'toCall', label: 'New, not called', n: t.toCall, tone: t.toCall ? 'text-blue-700' : '' },
    { kind: 'pollsDue', label: 'Polls to update', n: t.pollsDue, sub: t.pollsLate ? `${t.pollsLate} gone late` : 'due now', tone: t.pollsLate ? 'text-red-600' : t.pollsDue ? 'text-fuchsia-700' : '' },
    { kind: 'paymentsDue', label: 'Payments due', n: t.paymentsDue, sub: 'their pay date has come', tone: t.paymentsDue ? 'text-green-700' : '' },
    { kind: 'late', label: 'Follow-ups late', n: t.late, sub: `due over ${data.lateHours} h ago`, tone: t.late ? 'text-red-600' : '' },
    { kind: 'waiting', label: 'Waiting for someone', n: data.waiting.total, sub: data.waiting.passedAll ? `${data.waiting.passedAll} went through everyone` : 'no salesperson yet', tone: data.waiting.passedAll ? 'text-orange-600' : '' },
  ];
  return (
    <div className="space-y-4">
      <Head embedded title="Lead status" onRefresh={load} sub="Where every open lead is right now, and what is due. Tap a number to see the leads." />
      <HolidayBanner note={data.holiday} manager />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {tiles.map(x => (
          <button key={x.kind} onClick={() => x.n > 0 && show(x.kind)} disabled={!x.n}
            className="bg-white rounded-2xl border border-gray-100 px-3.5 py-3 text-left enabled:hover:border-purple-200 enabled:hover:shadow-sm transition-all min-w-0">
            <p className="text-[11px] font-semibold text-gray-500 truncate">{x.label}</p>
            <p className={`text-2xl font-black mt-0.5 tabular-nums ${x.tone || 'text-gray-900'}`}>{nf(x.n)}</p>
            {x.sub && <p className="text-[10px] text-gray-400 truncate">{x.sub}</p>}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="Each salesperson's leads">
        <h2 className="font-bold text-gray-800">Each salesperson's leads now</h2>
        <p className="text-xs text-gray-400 mb-3">Red: gone late. Polls are late {data.lateHours} h after they were due.</p>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm min-w-[940px]">
            <thead><tr className="text-[11px] text-gray-500">
              <th className="text-left py-2 px-2 font-bold sticky left-0 bg-white">Salesperson</th>
              {STATUS_COLS.map(c => <th key={c.kind} className="text-right py-2 px-2 font-bold whitespace-nowrap">{c.short}</th>)}
              <th className="text-right py-2 px-2 font-bold">Open</th>
              <th className="text-right py-2 px-2 font-bold">Late</th>
            </tr></thead>
            <tbody>
              {[...data.rows.map(r => ({ r, team: false })), ...(data.rows.length > 1 ? [{ r: { ...t, id: '', name: 'Team', role: '', active: true, inTurn: true, onLeave: false, paused: false } as LeadStatusRow, team: true }] : [])].map(({ r, team }) => (
                <tr key={r.id || 'team'} className={team ? 'border-t-2 border-gray-200 bg-gray-50/60' : 'border-t border-gray-100'}>
                  <td className={`py-2 px-2 sticky left-0 ${team ? 'bg-gray-50 font-black' : 'bg-white'}`}>
                    <b className="text-gray-800">{r.name}</b>
                    {!team && <span className="block text-[11px] text-gray-400">{r.role}{r.onLeave ? ' · on leave' : r.paused ? ' · paused' : !r.inTurn ? ' · not taking leads' : ''}</span>}
                  </td>
                  {STATUS_COLS.map(c => {
                    const n = r.stages[c.kind] || 0;
                    const extra = c.kind === 'Poll Sent' && r.pollsLate ? `${r.pollsLate} late` : c.kind === 'Interested' && r.paymentsDue ? `${r.paymentsDue} due` : '';
                    return (
                      <td key={c.kind} className="py-2 px-2 text-right whitespace-nowrap">
                        <Num n={n} strong={team} label={`${r.name}, ${c.label}`} onOpen={() => show(c.kind, team ? undefined : r)} />
                        {extra && <span className={`block text-[10px] font-bold ${c.kind === 'Poll Sent' ? 'text-red-600' : 'text-green-700'}`}>{extra}</span>}
                      </td>
                    );
                  })}
                  <td className="py-2 px-2 text-right"><Num n={r.open} strong label={`${r.name}, open leads`} onOpen={() => show('open', team ? undefined : r)} /></td>
                  <td className="py-2 px-2 text-right"><Num n={r.late} tone="text-red-600" strong={team} label={`${r.name}, late follow-ups`} onOpen={() => show('late', team ? undefined : r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {list && <StatusLeads auth={auth} kind={list.kind} person={list.person} onClose={() => setList(null)} onOpenLead={setOpen} />}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

// ── Daily EOD ────────────────────────────────────────────────

const EOD_BIG: [keyof Counts, string][] = [
  ['calls', 'Calls'], ['callLater', 'Call later'], ['cold', 'Cold'], ['pitched', 'Brochures'], ['visits', 'Visits'], ['meetings', 'Meetings'],
  ['pollsSent', 'Polls'], ['interested', 'Interested'], ['notInterested', 'Not interested'], ['converted', 'Converted'],
];
const MOOD: Record<string, string> = { Great: '🚀', Good: '😊', Okay: '😐', Tough: '😓' };
const STATUS_CHIP: Record<EodDayRow['status'], { text: string; cls: string }> = {
  sent:    { text: '✓ Sent', cls: 'bg-green-100 text-green-800' },
  auto:    { text: '📝 Made automatically', cls: 'bg-gray-100 text-gray-700' },
  due:     { text: '⏳ Not sent yet', cls: 'bg-amber-100 text-amber-800' },
  missing: { text: 'No report', cls: 'bg-red-100 text-red-700' },
  leave:   { text: '🌴 On leave', cls: 'bg-sky-100 text-sky-800' },
  off:     { text: 'Day off', cls: 'bg-gray-100 text-gray-500' },
};
const smallLine = (c: Counts) => `Talked to ${c.talked} · Not picked ${c.notPicked} · No response ${c.noResponse} · Future prospects ${c.future || 0}`
  + `${c.visitsCame || c.visitsMissed ? ` · Visits: ${c.visitsCame || 0} came, ${c.visitsMissed || 0} didn't` : ''}`;

const EodNumbers: React.FC<{ c: Counts; big?: boolean }> = ({ c, big }) => (
  <div className="grid grid-cols-4 sm:grid-cols-5 xl:grid-cols-10 gap-2">
    {EOD_BIG.map(([k, label]) => (
      <div key={k} className="rounded-xl bg-gray-50 py-2 text-center">
        <p className={`${big ? 'text-2xl' : 'text-lg'} font-black text-gray-800 tabular-nums`}>{nf(c[k] || 0)}</p>
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
      </div>
    ))}
  </div>
);

export const EodDayView: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [date, setDate] = useState('');
  const [data, setData] = useState<EodDayData | null>(null);
  const [loading, setLoading] = useState(true);
  // Only the latest day's answer counts
  const asked = useRef(0);
  const load = useCallback(async () => {
    const mine = ++asked.current;
    setLoading(true);
    try {
      const d = await crm.eodDay(auth, date);
      if (mine === asked.current) setData(d);
    } catch (e: any) { if (mine === asked.current) toast.error(e.message); }
    finally { if (mine === asked.current) setLoading(false); }
  }, [auth, date]);
  useEffect(() => { load(); }, [load]);
  if (!data) return loading ? <Spinner label="Loading the EOD reports…" /> : <Empty icon="⚠️" title="Could not load the EOD reports" />;
  const isToday = data.date === data.today;
  const sent = data.rows.filter(r => r.status === 'sent').length;
  return (
    <div className="space-y-4">
      <Head embedded title="Daily EOD" onRefresh={load}
        sub={`Each salesperson's day: the work done (every brochure and poll sent that day; Team numbers shows where the leads are now) and what they wrote. A report is made automatically at ${fmtHm(data.eodAt)} for anyone who didn't send one.`} />
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setDate(data.prev)} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50" aria-label="The day before">‹ Day before</button>
        <input type="date" value={data.date} max={data.today} onChange={e => e.target.value && setDate(e.target.value)} className={`${inputCls} !py-1.5 !w-44`} aria-label="Day" />
        <button onClick={() => data.next && setDate(data.next)} disabled={!data.next} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40" aria-label="The day after">Day after ›</button>
        {!isToday && <button onClick={() => setDate(data.today)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-purple-700 hover:underline">Today</button>}
        <span className="text-xs text-gray-500 ml-auto">{data.label}{data.rows.length ? ` · ${sent} of ${data.rows.length} sent` : ''}</span>
      </div>
      {data.holiday && <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">🏖️ {data.label}: {data.holiday.name}. The office was closed.</p>}
      {!data.holiday && !data.working && <p className="text-sm text-gray-500 bg-white border border-gray-100 rounded-2xl px-4 py-3">{data.label} is not a working day.</p>}

      <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
        {data.rows.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" role="region" aria-label="The team's day">
            <h2 className="font-bold text-gray-800 mb-3">The team's day</h2>
            <EodNumbers c={data.team} big />
            <p className="text-xs text-gray-400 mt-2">{smallLine(data.team)}</p>
          </div>
        )}
        {data.rows.length === 0 ? <Empty icon="📋" title="No reports for this day" sub="Nobody was working or calling." /> : data.rows.map(r => {
          const chip = STATUS_CHIP[r.status];
          const rep = r.report;
          return (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 space-y-3" role="region" aria-label={`${r.name}'s EOD`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-bold text-gray-800">{r.name}{rep?.mood && MOOD[rep.mood] ? <span className="ml-1.5" title={`Mood: ${rep.mood}`}>{MOOD[rep.mood]}</span> : null}</p>
                  <p className="text-[11px] text-gray-400">{r.role}{r.halfDay ? ` · half day off ${fmtHm(r.halfDay.from)}–${fmtHm(r.halfDay.to)}` : ''}</p>
                </div>
                <div className="text-right">
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${chip.cls}`}>{chip.text}</span>
                  {r.status === 'sent' && rep?.submittedAt && <p className="text-[10px] text-gray-400 mt-1">{fmtWhen(rep.submittedAt)}</p>}
                  {r.status === 'due' && <p className="text-[10px] text-gray-400 mt-1">made at {fmtHm(data.eodAt)} if not sent</p>}
                </div>
              </div>
              <EodNumbers c={r.counts} />
              <p className="text-xs text-gray-400">{smallLine(r.counts)}</p>
              {rep?.typed && <p className="text-xs text-gray-500">They typed: {rep.typed.calls} calls · {rep.typed.demos} demos · {rep.typed.conversions} conversions</p>}
              {rep && [['🌟 How the day went', rep.highlights, '#10B981'], ['⚠️ Challenges', rep.challenges, '#F97316'], ['📋 Plan for tomorrow', rep.plan, '#3B82F6']].filter(([, text]) => text).map(([title, text, color]) => (
                <div key={title} className="rounded-xl px-3 py-2.5" style={{ background: `${color}10`, borderLeft: `3px solid ${color}` }}>
                  <p className="text-[11px] font-bold text-gray-600">{title}</p>
                  <p className="text-sm text-gray-800 whitespace-pre-line mt-0.5">{text}</p>
                </div>
              ))}
              {r.status === 'auto' && <p className="text-[11px] text-gray-400">They didn't write one: the numbers were filled in from the CRM at the end of the day.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
